import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, IconChevronDown} from '@veneer/core';

import KBLink from '../KBLink/KBLink';

import useStyles from './styles';
import {PanelTheme} from './types';

type PropsBase = {
    collapsable?: false;
    children: React.ReactNode;
    footer?: React.ReactNode;
    title?: string;
    style?: Record<string, string>;
    className?: string;
    icon?: React.ReactElement;
    actions?: React.ReactNode;
    componentTheme?: PanelTheme;
    testid?: string;
    defaultOpen?: undefined;
    id?: string;
    helpLink?: string;
};

// Force some props when expandable for a11y
type PropsExpandable = Omit<PropsBase, 'defaultOpen' | 'id' | 'title' | 'collapsable'> & {
    collapsable: true;
    defaultOpen?: boolean;
    id: string;
    title: string;
};

const Panel = (props: PropsBase | PropsExpandable) => {
    const {
        children,
        footer,
        title,
        style,
        className = '',
        icon,
        componentTheme = 'default',
        testid,
        defaultOpen = true,
        id,
        collapsable = false,
        helpLink,
        actions,
    } = props;

    const {t} = useTranslation(['Panel']);
    const [open, setOpen] = useState(defaultOpen);

    const toggle = useCallback(() => {
        setOpen(curr => !curr);
    }, []);

    const styles = useStyles(componentTheme, open, collapsable);

    return (
        <div
            {...(testid ? {'data-testid': testid} : {})}
            {...(id ? {id: id} : {})}
            style={{
                ...style,
            }}
            className={`${styles.panel} ${className}`}
        >
            <div className={styles.wrapper}>
                {title && (
                    <div className={styles.panelHeader}>
                        <div className={styles.panelIconWrapper}>
                            {icon && <div className={styles.iconWrapper}>{icon}</div>}
                            <h5 className={`subtitle-regular ${styles.title}`}>
                                <span>{title}</span>
                                {helpLink && <KBLink href={helpLink} />}
                            </h5>
                        </div>

                        {actions && <div className={styles.panelActions}>{actions}</div>}

                        {collapsable && (
                            <div className={styles.expandButton}>
                                <Button
                                    small
                                    type="button"
                                    aria-expanded={open}
                                    aria-controls={id}
                                    onClick={toggle}
                                    appearance="ghost"
                                    aria-label={
                                        open
                                            ? t('Panel:closeButton', 'Collapse {{title}} panel', {title: title})
                                            : t('Panel:openButton', 'Expand {{title}} panel', {title: title})
                                    }
                                    leadingIcon={<IconChevronDown className={styles.toggle} />}
                                />
                            </div>
                        )}
                    </div>
                )}

                <div
                    className={styles.children}
                    hidden={!open}
                >
                    <div className={styles.childrenInner}>
                        <div className={styles.childrenPadding}>
                            {children}
                            {footer && <div className={styles.footer}>{footer}</div>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Panel;
